<template>
    <LsInfoRow
        class="ls-info-row-upcoming"
        :left-image="require('./assets/calendar.svg')"
        :label="label"
        :value="value"
    >
        <img
            v-if="schedule.next_service_within_48_hours && schedule.status !== 'cancelled'"
            :src="require('./assets/time.svg')"
        />
        <img v-else-if="schedule.is_paused" :src="require('./assets/pause.svg')" />
    </LsInfoRow>
</template>

<script>
import VueTypes from 'vue-types';
import _ from 'lodash';
import LsInfoRow from '../LsInfoRow/LsInfoRow.vue';
import { lsHelpers } from '@/helpers';

const MANUAL_PRICE = 'manual_price';
const MANUAL_QUOTE = 'manualquote';
// Matches the placeholder the sibling pricing and status rows show.
const EMPTY_DISPLAY = '- - - -';

export default {
    name: 'LsInfoRowUpcoming',
    components: {
        LsInfoRow,
    },
    props: {
        schedule: VueTypes.shape({
            status: VueTypes.string,
            type: VueTypes.string,
            __typename: VueTypes.string.isRequired,
            manualquote: VueTypes.oneOfType([
                VueTypes.shape({
                    price: VueTypes.oneOfType([
                        VueTypes.shape({
                            schedule_at: VueTypes.string.isRequired,
                        }).loose,
                        VueTypes.oneOfType([null]),
                    ]),
                }).loose,
                VueTypes.oneOfType([null]),
            ]),
            next_customer_starts_at: VueTypes.string.isRequired,
            next_customer_ends_at: VueTypes.string,
            next_service_within_48_hours: VueTypes.bool,
            is_paused: VueTypes.bool,
        }).isRequired.loose,
        quote: VueTypes.oneOfType([
            VueTypes.shape({
                setAt: VueTypes.oneOfType([VueTypes.string, VueTypes.oneOfType([null])]),
                scheduleEvent: VueTypes.oneOfType([
                    VueTypes.shape({
                        startsAt: VueTypes.oneOfType([VueTypes.string, VueTypes.oneOfType([null])]),
                    }).loose,
                    VueTypes.oneOfType([null]),
                ]),
            }).loose,
            VueTypes.oneOfType([null]),
        ]).def(null),
        quoteLoading: VueTypes.bool.def(false),
    },
    computed: {
        isManualQuote() {
            return this.schedule.type === MANUAL_QUOTE;
        },
        isManualPrice() {
            return (
                !this.isManualQuote && _.get(this.schedule, 'service.price_type') === MANUAL_PRICE
            );
        },
        // Do not swap to `contractorStartsAt`: it arrives as a UTC datetime and shifts back a
        // day for any admin west of UTC.
        manualPriceValue() {
            if (this.quoteLoading || _.isNil(this.quote)) {
                return EMPTY_DISPLAY;
            }

            if (_.isNil(_.get(this.quote, 'setAt'))) {
                return 'N/A';
            }

            const startsAt = _.get(this.quote, 'scheduleEvent.startsAt');

            return _.isNil(startsAt) ? 'N/A' : lsHelpers.dateRange({ start: startsAt });
        },
        label() {
            // Stays 'Service Date' in both states; 'Upcoming' would imply someone proposed it.
            if (this.isManualPrice) {
                return 'Service Date';
            }

            return this.isPricedManualquote || this.isRecommendationOrRequired
                ? 'Service Date'
                : 'Upcoming';
        },
        isPricedManualquote() {
            return (
                this.schedule.type === 'manualquote' && _.get(this.schedule.manualquote, 'price')
            );
        },
        isRecommendationOrRequired() {
            return (
                this.schedule.__typename === 'Recommendation' ||
                this.schedule.__typename === 'RequiredService'
            );
        },
        value() {
            // Must stay first: the legacy arms answer from the schedule, which carries a next
            // customer date whether or not this request was priced.
            if (this.isManualPrice) {
                return this.manualPriceValue;
            }

            if (
                this.isPricedManualquote &&
                this.schedule.next_customer_starts_at !==
                    this.schedule.manualquote.price.schedule_at
            ) {
                return lsHelpers.dateRange({
                    start: this.schedule.manualquote.price.schedule_at,
                });
            }
            const { status } = this.schedule;

            if (
                status === 'expired' ||
                status === 'rejected' ||
                status === 'ended' ||
                status === 'cancelled'
            ) {
                return 'N/A';
            }

            return lsHelpers.dateRange({
                start: this.schedule.next_customer_starts_at,
                end: this.schedule.next_customer_ends_at,
            });
        },
    },
};
</script>
